import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Bot,
  CheckCircle2,
  Download,
  FileAudio,
  Film,
  Languages,
  Loader2,
  XCircle,
} from "lucide-react";

import api, { sameOriginApiUrl, unwrapList } from "utils/api";
import { API_ENDPOINTS } from "config/api";
import { downloadFileFromUrl } from "utils/download";
import { detectTextLang, normalizeLang, translations } from "utils/i18n";
import { toast } from "utils/toast";
import { useUser } from "context/userContext";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Skeleton } from "components/ui/skeleton";
import { useSidebar } from "components/ui/sidebar";

const POLL_INTERVAL_MS = 20_000;
// Faster than the badge-only poll above — while the panel is actually open
// and someone's looking at a "rendering…" row, they shouldn't have to close
// and reopen it to see it flip to ready.
const OPEN_LIST_POLL_INTERVAL_MS = 5_000;

const RESOURCE_ICONS = {
  transcription: FileAudio,
  translation: Languages,
  review: Bot,
  video_export: Film,
};

const JOB_DETAIL_ENDPOINTS = {
  transcription: API_ENDPOINTS.TRANSCRIPTION.DETAIL,
  translation: API_ENDPOINTS.TRANSLATION.DETAIL,
  review: API_ENDPOINTS.REVIEW.DETAIL,
  video_export: API_ENDPOINTS.EXPORT.VIDEO_DETAIL,
};

function relativeTime(iso, language) {
  const timestamp = new Date(iso).getTime();
  if (!Number.isFinite(timestamp)) return "";

  const seconds = Math.round((timestamp - Date.now()) / 1000);
  const absoluteSeconds = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(language, { numeric: "auto" });

  if (absoluteSeconds < 60) return formatter.format(seconds, "second");
  if (absoluteSeconds < 3600) return formatter.format(Math.round(seconds / 60), "minute");
  if (absoluteSeconds < 86400) return formatter.format(Math.round(seconds / 3600), "hour");
  if (absoluteSeconds < 604800) return formatter.format(Math.round(seconds / 86400), "day");

  return new Intl.DateTimeFormat(language, { month: "short", day: "numeric" }).format(timestamp);
}

function notificationTitle(notification, tr) {
  return tr.copy[notification.resource_type]?.[notification.outcome] ?? tr.genericUpdate;
}

function notificationPath(notification, job) {
  const transcriptId = notification.payload?.transcript_id;
  if (!transcriptId) return null;

  const source = job?.source ?? job?.transcript?.source;
  return notification.resource_type === "video_export" || source === "caption"
    ? `/caption/${transcriptId}`
    : `/transcribe/${transcriptId}`;
}

function NotificationSkeleton({ label }) {
  return (
    <div className="space-y-1 p-1" aria-label={label}>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 px-3 py-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ tr }) {
  return (
    <div className="flex min-h-44 flex-col items-center justify-center px-6 py-8 text-center">
      <span className="mb-3 flex size-9 items-center justify-center rounded-full bg-secondary">
        <Bell className="size-4 text-muted-foreground" aria-hidden="true" />
      </span>
      <span className="text-sm font-medium text-foreground">{tr.allCaughtUp}</span>
      <span className="mt-1 text-xs leading-relaxed text-muted-foreground">{tr.emptyDescription}</span>
    </div>
  );
}

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const language = normalizeLang(user?.lang);
  const tr = translations[language].notifications;

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [updatingIds, setUpdatingIds] = useState(() => new Set());
  const [openingIds, setOpeningIds] = useState(() => new Set());
  // Keyed by notification id; present (value null or a 0-100 number) while a
  // download is in flight for that row, absent otherwise.
  const [downloadProgressById, setDownloadProgressById] = useState(() => new Map());
  const [unavailableIds, setUnavailableIds] = useState(() => new Set());
  const listRequestRef = useRef(0);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return 0;
    }
    const { data } = await api.get(API_ENDPOINTS.NOTIFICATION.UNREAD_COUNT);
    const count = Math.max(0, Number(data?.count) || 0);
    setUnreadCount(count);
    return count;
  }, [user]);

  // Poll only while the document is visible. Returning to the tab triggers
  // an immediate refresh before the normal interval resumes.
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      clearTimeout(timer);
      try {
        await refreshUnreadCount();
      } catch {
        // A badge is non-critical; the next poll or panel open retries it.
      } finally {
        if (!cancelled && document.visibilityState === "visible") {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    const handleVisibilityChange = () => {
      clearTimeout(timer);
      if (document.visibilityState === "visible") poll();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (document.visibilityState === "visible") poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshUnreadCount, user]);

  useEffect(
    () => () => {
      listRequestRef.current += 1;
    },
    []
  );

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    const requestId = ++listRequestRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get(API_ENDPOINTS.NOTIFICATION.LIST, { params: { page_size: 20 } });
      if (requestId !== listRequestRef.current) return;
      setNotifications(unwrapList(data));
      setNextUrl(Array.isArray(data) ? null : data?.next ?? null);
    } catch {
      if (requestId === listRequestRef.current) setLoadError(true);
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, [user]);

  function handleOpenChange(nextOpen) {
    setOpen(nextOpen);
    if (nextOpen) {
      loadNotifications();
      refreshUnreadCount().catch(() => {});
    }
  }

  // Re-fetches the first page and merges it into whatever's already
  // rendered — by id, so an in-place status change (e.g. a video-export row
  // moving from pending to completed) updates that row instead of
  // reloading the whole list and losing scroll position or anything
  // appended via "load more".
  const refreshOpenList = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get(API_ENDPOINTS.NOTIFICATION.LIST, { params: { page_size: 20 } });
      const page = unwrapList(data);
      const pageIds = new Set(page.map((notification) => notification.id));
      setNotifications((current) => [...page, ...current.filter((notification) => !pageIds.has(notification.id))]);
    } catch {
      // Silent — the next tick retries, and the badge poll above still
      // reflects reality either way.
    }
  }, [user]);

  useEffect(() => {
    if (!open || !user) return undefined;
    const id = setInterval(refreshOpenList, OPEN_LIST_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [open, user, refreshOpenList]);

  async function loadMore() {
    if (!nextUrl || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(sameOriginApiUrl(nextUrl));
      const page = unwrapList(data);
      setNotifications((current) => {
        const existingIds = new Set(current.map((notification) => notification.id));
        return [...current, ...page.filter((notification) => !existingIds.has(notification.id))];
      });
      setNextUrl(Array.isArray(data) ? null : data?.next ?? null);
    } catch {
      toast.error(tr.loadError);
    } finally {
      setLoadingMore(false);
    }
  }

  async function setReadState(notification, isRead) {
    if (notification.is_read === isRead || updatingIds.has(notification.id)) return;

    const previous = notification;
    setUpdatingIds((current) => new Set(current).add(notification.id));
    setNotifications((current) =>
      current.map((item) => (item.id === notification.id ? { ...item, is_read: isRead } : item))
    );
    setUnreadCount((count) =>
      count == null ? count : Math.max(0, count + (isRead ? -1 : 1))
    );

    try {
      const { data } = await api.patch(API_ENDPOINTS.NOTIFICATION.DETAIL(notification.id), {
        is_read: isRead,
      });
      setNotifications((current) =>
        current.map((item) => (item.id === notification.id ? data : item))
      );
    } catch {
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id && item.is_read === isRead ? previous : item
        )
      );
      toast.error(tr.updateError);
    } finally {
      setUpdatingIds((current) => {
        const next = new Set(current);
        next.delete(notification.id);
        return next;
      });
      refreshUnreadCount().catch(() => {});
    }
  }

  async function markAllRead() {
    if (markingAllRead || unreadCount === 0) return;
    const previous = notifications;
    setMarkingAllRead(true);
    setNotifications((current) => current.map((notification) => ({ ...notification, is_read: true })));
    setUnreadCount(0);
    try {
      await api.post(API_ENDPOINTS.NOTIFICATION.READ_ALL, {});
    } catch {
      setNotifications(previous);
      toast.error(tr.updateError);
    } finally {
      setMarkingAllRead(false);
      refreshUnreadCount().catch(() => {});
    }
  }

  async function openNotification(notification) {
    if (openingIds.has(notification.id)) return;
    if (!notification.is_read) setReadState(notification, true);

    const detailEndpoint = JOB_DETAIL_ENDPOINTS[notification.resource_type];
    const transcriptId = notification.payload?.transcript_id;
    if (!detailEndpoint || !notification.resource_id || !transcriptId) {
      setUnavailableIds((current) => new Set(current).add(notification.id));
      return;
    }

    setOpeningIds((current) => new Set(current).add(notification.id));
    try {
      const { data: job } = await api.get(detailEndpoint(notification.resource_id));
      const path = notificationPath(notification, job);
      if (!path) {
        setUnavailableIds((current) => new Set(current).add(notification.id));
        return;
      }
      setOpen(false);
      navigate(path);
    } catch (error) {
      if (error?.response?.status === 404) {
        setUnavailableIds((current) => new Set(current).add(notification.id));
      } else {
        const path = notificationPath(notification);
        if (path) {
          setOpen(false);
          navigate(path);
        }
      }
    } finally {
      setOpeningIds((current) => {
        const next = new Set(current);
        next.delete(notification.id);
        return next;
      });
      refreshUnreadCount().catch(() => {});
    }
  }

  // Lets a completed video-export notification be saved straight from the
  // panel, without navigating to the transcript just to press Download
  // again there — fetches the job for its (short-lived, presigned)
  // video_url, same as opening the notification does, but downloads that
  // URL instead of following it.
  async function downloadVideo(event, notification) {
    event.stopPropagation();
    if (downloadProgressById.has(notification.id)) return;
    if (!notification.resource_id) {
      setUnavailableIds((current) => new Set(current).add(notification.id));
      return;
    }
    if (!notification.is_read) setReadState(notification, true);

    setDownloadProgressById((current) => new Map(current).set(notification.id, null));
    try {
      const { data: job } = await api.get(
        API_ENDPOINTS.EXPORT.VIDEO_DETAIL(notification.resource_id)
      );
      if (!job?.video_url) {
        setUnavailableIds((current) => new Set(current).add(notification.id));
        return;
      }
      const name = notification.payload?.transcript_name || tr.untitledTranscript;
      await downloadFileFromUrl(job.video_url, `${name}.mp4`, (percent) =>
        setDownloadProgressById((current) => new Map(current).set(notification.id, percent))
      );
    } catch (error) {
      if (error?.response?.status === 404) {
        setUnavailableIds((current) => new Set(current).add(notification.id));
      } else {
        toast.error(tr.downloadError);
      }
    } finally {
      setDownloadProgressById((current) => {
        const next = new Map(current);
        next.delete(notification.id);
        return next;
      });
    }
  }

  const badgeLabel = unreadCount ? tr.unreadCount(unreadCount) : null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={badgeLabel ? `${tr.title}, ${badgeLabel}` : tr.title}
          className={cn("relative shrink-0 text-muted-foreground", collapsed && "rounded-full")}
        >
          <Bell aria-hidden="true" />
          {unreadCount > 0 ? (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-card"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="top"
        align={collapsed ? "start" : "end"}
        sideOffset={8}
        lang={language}
        dir={language === "ar" ? "rtl" : "ltr"}
        className="w-80 p-0"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="m-0 text-sm font-semibold text-foreground">{tr.title}</h2>
          {unreadCount > 0 ? (
            <button
              type="button"
              data-slot="button"
              disabled={markingAllRead}
              onClick={markAllRead}
              className="border-0 bg-transparent p-0 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
            >
              {tr.markAllRead}
            </button>
          ) : null}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {loading ? <NotificationSkeleton label={tr.loading} /> : null}

          {!loading && loadError ? (
            <div className="flex min-h-44 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
              <span className="text-sm text-muted-foreground">{tr.loadError}</span>
              <Button type="button" variant="outline" size="sm" onClick={loadNotifications}>
                {tr.retry}
              </Button>
            </div>
          ) : null}

          {!loading && !loadError && notifications.length === 0 ? <EmptyState tr={tr} /> : null}

          {!loading && !loadError && notifications.length > 0 ? (
            <div role="list" className="divide-y divide-border">
              {notifications.map((notification) => {
                const ResourceIcon = RESOURCE_ICONS[notification.resource_type] ?? Bell;
                const unread = !notification.is_read;
                const failed = notification.outcome === "failed";
                const pending = notification.outcome === "pending";
                const unavailable = unavailableIds.has(notification.id);
                const opening = openingIds.has(notification.id);
                const downloading = downloadProgressById.has(notification.id);
                const downloadPercent = downloadProgressById.get(notification.id);
                const downloadable =
                  notification.resource_type === "video_export" && !failed && !pending && !unavailable;
                const transcriptName = notification.payload?.transcript_name || tr.untitledTranscript;

                return (
                  <div
                    key={notification.id}
                    role="listitem"
                    className={cn("group flex items-stretch", unread && "bg-accent/40")}
                  >
                    <button
                      type="button"
                      data-slot="button"
                      disabled={opening}
                      onClick={() => openNotification(notification)}
                      className="flex min-w-0 flex-1 items-start gap-3 border-0 bg-transparent px-3 py-3 text-start outline-none transition-colors hover:bg-accent/60 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-wait"
                    >
                      <span className="relative mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                        {opening ? (
                          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                        ) : pending ? (
                          <ResourceIcon className="size-4 animate-pulse" aria-hidden="true" />
                        ) : (
                          <ResourceIcon className="size-4" aria-hidden="true" />
                        )}
                        {!opening && !pending ? (
                          failed ? (
                            <XCircle
                              className="absolute -bottom-0.5 -right-0.5 size-3.5 fill-card text-destructive"
                              aria-hidden="true"
                            />
                          ) : (
                            <CheckCircle2
                              className="absolute -bottom-0.5 -right-0.5 size-3.5 fill-card text-emerald-500"
                              aria-hidden="true"
                            />
                          )
                        ) : null}
                        {!opening && pending ? (
                          <Loader2
                            className="absolute -bottom-0.5 -right-0.5 size-3.5 animate-spin fill-card text-muted-foreground"
                            aria-hidden="true"
                          />
                        ) : null}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            "block truncate text-sm text-foreground",
                            unread ? "font-semibold" : "font-medium"
                          )}
                        >
                          {notificationTitle(notification, tr)}
                        </span>
                        <span
                          lang={detectTextLang(transcriptName)}
                          className="mt-0.5 block truncate text-xs text-muted-foreground"
                        >
                          {transcriptName}
                        </span>
                        {failed && notification.payload?.refunded === true ? (
                          <span className="mt-1 block text-xs leading-snug text-muted-foreground">
                            {tr.refunded}
                          </span>
                        ) : null}
                        {unavailable ? (
                          <span className="mt-1 block text-xs font-medium text-destructive">
                            {tr.unavailable}
                          </span>
                        ) : null}
                        <time
                          dateTime={notification.created_at}
                          className="mt-1.5 block text-[11px] text-muted-foreground/80"
                        >
                          {relativeTime(notification.created_at, language)}
                        </time>
                      </span>
                    </button>

                    {downloadable ? (
                      <button
                        type="button"
                        data-slot="button"
                        disabled={downloading}
                        onClick={(event) => downloadVideo(event, notification)}
                        aria-label={
                          downloading && Number.isFinite(downloadPercent)
                            ? `${tr.download} ${downloadPercent}%`
                            : tr.download
                        }
                        title={
                          downloading && Number.isFinite(downloadPercent)
                            ? `${tr.download} ${downloadPercent}%`
                            : tr.download
                        }
                        className="flex shrink-0 items-center gap-1.5 border-0 bg-transparent px-3 text-muted-foreground outline-none transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-wait disabled:opacity-60"
                      >
                        {downloading ? (
                          <>
                            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                            {Number.isFinite(downloadPercent) ? (
                              <span className="text-xs tabular-nums">{downloadPercent}%</span>
                            ) : null}
                          </>
                        ) : (
                          <Download className="size-4" aria-hidden="true" />
                        )}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {!loading && !loadError && nextUrl ? (
          <div className="border-t border-border p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={loadingMore}
              onClick={loadMore}
              className="w-full text-muted-foreground"
            >
              {loadingMore ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              {tr.loadMore}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
